import { cn } from "@/lib/utils";
import { MAND, MAX, MAND_PRIMARY, MAX_PRIMARY } from "./config";

const kind = (fdi) => {
  const q = Number(String(fdi)[0]);
  const d = Number(String(fdi)[1]);
  if (q >= 5) return d >= 4 ? "premolar" : d === 3 ? "canine" : "incisor";
  return d >= 6 ? "molar" : d >= 4 ? "premolar" : d === 3 ? "canine" : "incisor";
};
const WIDTH = { molar: 34, premolar: 26, canine: 24, incisor: 22 };
const PATH = {
  molar: "M4 10 C4 3 10 2 17 4 C24 2 30 3 30 10 L29 22 C28 30 24 36 22 44 L19 44 L17 34 L15 44 L12 44 C10 36 6 30 5 22 Z",
  premolar: "M3 10 C3 3 8 2 13 3 C18 2 23 3 23 10 L22 22 C21 32 17 38 15 46 L11 46 C9 38 5 32 4 22 Z",
  canine: "M2 12 C2 4 7 1 12 1 C17 1 22 4 22 12 L20 24 C19 34 15 42 13 50 L11 50 C9 42 5 34 4 24 Z",
  incisor: "M2 6 C2 2 6 1 11 1 C16 1 20 2 20 6 L19 22 C18 32 14 40 12 48 L10 48 C8 40 4 32 3 22 Z",
};
const TONES = {
  implant: "fill-[#00A85A] stroke-[#008F4D] text-white",
  extraction: "fill-[#F59E0B] stroke-[#B45309] text-white",
  natural: "fill-[#DCEFE5] stroke-[#00A85A] text-[#008F4D] dark:fill-[#00A85A]/25 dark:text-[#3FD98A]",
};

function Tooth({ fdi, flip, crownDown, mark, isLocked, edentulous, onToggle, testid, small }) {
  const k = kind(fdi);
  const w = WIDTH[k];
  const tone = mark ? TONES[mark] : edentulous
    ? "fill-transparent stroke-[#B7C3C7] text-[#8B9698] [stroke-dasharray:3_2]"
    : "fill-white stroke-[#B7C3C7] text-[#687173] dark:fill-white/5 dark:stroke-[#3A4A52] dark:text-[#8B9698]";
  return (
    <button type="button" disabled={isLocked} onClick={() => onToggle(fdi)} aria-pressed={!!mark}
      aria-label={`Tooth ${fdi}${mark ? `, ${mark}` : ""}${isLocked ? ", locked" : ""}`} data-testid={`${testid}-tooth-${fdi}`}
      className={cn("group flex flex-col items-center gap-0.5 rounded-md p-0.5 transition-transform motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A85A]",
        isLocked ? "cursor-not-allowed opacity-30" : "hover:-translate-y-0.5", (flip || crownDown) && "flex-col-reverse")}>
      <svg viewBox={`0 0 ${w + 2} 52`} className={cn("w-auto overflow-visible", small ? "h-5 sm:h-8" : "h-7 sm:h-[46px]", flip && "rotate-180", tone)}>
        <path d={PATH[k]} transform={crownDown ? "translate(0 52) scale(1 -1)" : undefined} data-crown={crownDown ? "down" : "up"} strokeWidth="1.6" className="transition-colors motion-reduce:transition-none group-hover:stroke-[#00A85A]" />
      </svg>
      <span className={cn("text-[10px] font-bold tabular-nums", mark ? "text-[#101516] dark:text-white" : "text-[#8B9698]")}>{fdi}</span>
    </button>
  );
}

function Row({ teeth, flip, crownDown, small, ...rest }) {
  const half = teeth.length / 2;
  return (
    <div className={cn("flex justify-center", crownDown ? "items-start" : "items-end")}>
      <div className="flex gap-px border-r border-dashed border-[#CBD5D9] pr-1 dark:border-[#2A3A42]">{teeth.slice(0, half).map((n) => <Tooth key={n} fdi={n} flip={flip} crownDown={crownDown} small={small} mark={rest.marks[n]} isLocked={rest.locked(n)} {...rest} />)}</div>
      <div className="flex gap-px pl-1">{teeth.slice(half).map((n) => <Tooth key={n} fdi={n} flip={flip} crownDown={crownDown} small={small} mark={rest.marks[n]} isLocked={rest.locked(n)} {...rest} />)}</div>
    </div>
  );
}

// Mandible (48→38) on top, Maxilla (18→28) at the bottom — the doctor's chair-side view.
// Implant chart: Maxilla on top (crowns down), Mandible below (crowns up) — crowns meet at the bite line.
function AnatomicalChart({ props, testid, readOnly }) {
  return (
    <div className={cn("overflow-x-auto thin-scroll", readOnly && "pointer-events-none")} data-testid={testid} data-orientation="anatomical">
      <div className="mx-auto w-max space-y-1 py-1">
        <div className="text-center text-[10px] font-bold uppercase tracking-wider text-[#8B9698]" data-testid={`${testid}-label-maxilla`}>Maxilla (upper)</div>
        <Row teeth={MAX} crownDown {...props} />
        <div className="mx-auto h-px w-full bg-[#00A85A]/40" data-testid={`${testid}-bite-line`} />
        <Row teeth={MAND} {...props} />
        <div className="text-center text-[10px] font-bold uppercase tracking-wider text-[#8B9698]" data-testid={`${testid}-label-mandible`}>Mandible (lower)</div>
      </div>
    </div>
  );
}

export function ToothChart({ marks = {}, onToggle = () => {}, locked = () => false, edentulous, dentition = "Permanent", testid = "tooth-chart", anatomical = false, readOnly = false }) {
  const props = { marks, onToggle, locked, edentulous, testid };
  const perm = dentition !== "Primary";
  const prim = dentition === "Primary" || dentition === "Mixed";
  if (anatomical) return <AnatomicalChart props={props} testid={testid} readOnly={readOnly} />;
  return (
    <div className="overflow-x-auto thin-scroll" data-testid={testid}>
      <div className="mx-auto w-max space-y-1 py-1">
        <div className="text-center text-[10px] font-bold uppercase tracking-wider text-[#8B9698]">Mandible</div>
        {perm && <Row teeth={MAND} {...props} />}
        {prim && <Row teeth={MAND_PRIMARY} small={perm} {...props} />}
        <div className="mx-auto h-px w-full bg-[#E2E8F0] dark:bg-[#223139]" />
        {prim && <Row teeth={MAX_PRIMARY} flip small={perm} {...props} />}
        {perm && <Row teeth={MAX} flip {...props} />}
        <div className="text-center text-[10px] font-bold uppercase tracking-wider text-[#8B9698]">Maxilla</div>
      </div>
    </div>
  );
}

export function ChartLegend({ items }) {
  const sw = { implant: "bg-[#00A85A]", extraction: "bg-[#F59E0B]", natural: "bg-[#DCEFE5] border border-[#00A85A]", empty: "border border-dashed border-[#B7C3C7]", unselected: "bg-white border border-[#B7C3C7]", locked: "bg-[#B7C3C7] opacity-40" };
  return (
    <div className="flex flex-wrap gap-3 text-[11px] text-[#687173] dark:text-[#8B9698]" data-testid="chart-legend">
      {items.map(([k, label]) => <span key={k} className="flex items-center gap-1.5"><span className={cn("h-3 w-3 rounded-sm", sw[k])} />{label}</span>)}
    </div>
  );
}
